<template>
  <div class="liff-reservation-schedule">
    <h2 class="wizard-mode-layout__page-title" v-if="bookingPreset?.config?.schedule_page?.title">{{ bookingPreset.config.schedule_page.title }}</h2>
    <h4 class="text-center my-2" v-if="selectedBranch || selectedStaff">
      <div class="liff-reservation-schedule__branch-staff">
        <div v-if="selectedBranch?.id" class="liff-reservation-schedule__branch-staff__name">{{ deepGet(selectedBranch, 'name') }}</div>
        <div v-if="selectedStaff?.id" class="liff-reservation-schedule__branch-staff__img">
          <img v-if="deepGet(selectedStaff, 'img')" :src="deepGet(selectedStaff, 'img')" alt="staff">
          <img v-else src="./images/staff.png" alt="staff">
          {{ deepGet(selectedStaff, 'name') }}
        </div>
      </div>
    </h4>
    <h4 class="text-center my-2" v-if="selectedServices.length > 0">
      <div class="liff-reservation-schedule__services">
        <div class="liff-reservation-schedule__services__img">
          <template v-for="img in selectedServices">
            <img v-if="img.image" :src="img.image" alt="service" :key="img._id">          
          </template>
        </div>
        {{ selectedServices.map((service) => service.title).join('、') }}
      </div>
    </h4>
    <div class="s-mb-3">請選擇日期：<span v-if="date">已選擇 {{ selectedDateInString }}</span></div>
    <div class="liff-reservation-schedule__date s-mb-4">
      <div v-if="!bookingPreset || isFetchingMonthlyShifts" class="wizard-mode-layout__loading-overlay">
        <div class="wizard-mode-layout__loading-spinner"></div>
        <div class="wizard-mode-layout__loading-text">載入中...</div>
      </div>
      <Datepicker
        placeholder="請輸入查詢開始時間"
        format="yyyy/MM/dd"
        v-model="date"
        :language="zh"
        :disabled-dates="{ customPredictor: dateIsUnavailable }"
        calendar-class="datepicker-calendar"
        wrapper-class="datepicker-wrapper"
        bootstrap-styling
        inline
        @opened="handleCalendarOpened"
        @changedMonth="handleMonthChange"
        @input="handleDateChanged"
      >
      </Datepicker>
    </div>

    <div class="s-mb-3" v-if="!dataModel.startAtFieldFormatIsDate">
      請選擇時間：
      <span v-if="time">
        已選擇 {{ selectedDateAndTimeLabel }}
      </span>
    </div>
    <div class="liff-reservation-schedule__timer" v-if="!dataModel.startAtFieldFormatIsDate">
      <div v-if="isCheckingOccupancy" class="wizard-mode-layout__loading-overlay">
        <div class="wizard-mode-layout__loading-spinner"></div>
        <div class="wizard-mode-layout__loading-text">載入中...</div>
      </div>
      <div
        v-for="timeOption in timeOptions"
        :key="timeOption.value"
        class="liff-reservation-schedule__time-option"
        :class="{
          'selected': time === timeOption.value,
          'disabled': timeIsUnavailable(timeOption.value)
        }"
        @click="selectTime(timeOption.value)"
      >
        <input
          type="radio"
          :id="timeOption.value"
          :value="timeOption.value"
          v-model="time"
          class="liff-reservation-schedule__time-input"
          :disabled="timeIsUnavailable(timeOption.value)"
        />
        <label :for="timeOption.value">{{ timeOption.text || timeOption.value }}</label>
      </div>
    </div>

    <p class="text-danger text-center mt-3" v-if="!isCheckingAvailability && !passedAvailabilityCheck">無法預約該時段</p>

    <div class="liff-reservation-schedule__notice">
      <div class="liff-reservation-schedule__notice-divider"></div>
      <div class="s-mb-3 s-font-medium s-pt-3">注意事項</div>
      <div class="s-text-sm s-text-gray" ref="reservationNotes" v-html="reservationNotes"></div>

      <div class="booking-tip-modal">
        <Popup v-if="bookingTip" ref="bookingTipPopup" :title="bookingTip.form_title" panelHeight="auto">
          <div class="s-mb-5">
          {{ bookingTip.form_desc }}
          </div>
        <div slot="actions" class="s-flex s-flex-col s-space-y-2">
          <MemberButton
            v-if="bookingTip.button_top.enabled"
            :button-text="bookingTip.button_top.button_text"
            :type="bookingTip.button_top.button_action"
            :button-url="bookingTip.button_top.button_url"
          >
          </MemberButton>

          <template v-if="bookingTip.button_bottom.enabled">
            <SharedButton v-if="bookingTip.button_bottom.button_action=='Exit'" class="s-btn-outline-primary" @click="$refs.bookingTipPopup.close()">
              {{ bookingTip.button_bottom.button_text }}
            </SharedButton>
            <MemberButton v-else
              :button-text="bookingTip.button_bottom.button_text"
              :type="bookingTip.button_bottom.button_action"
              :button-url="bookingTip.button_bottom.button_url">
            </MemberButton>
          </template>
        </div>
        </Popup>
      </div>

    </div>

    <div class="wizard-mode-layout__footer-navigation">
      <div v-if="date && (time || dataModel.startAtFieldFormatIsDate)" class="s-flex s-justify-between s-items-center s-mb-3">
        <div class="s-font-medium">
          <p>
            預約時間：
            <span class="s-text-primary">
              {{ selectedDateAndTimeLabel }}
            </span>
          </p>
          <div v-if="endDateTimeForDisplay && !dataModel.shouldHideEndTime">
            預計結束時間：
            <span class="s-text-primary">
              {{ endDateTimeForDisplay }}
            </span>
          </div>
        </div>
        <div class="s-text-primary cursor-pointer s-font-medium s-flex-shrink-0" @click="resetDate()">重選</div>
      </div>
      <div class="wizard-mode-layout__footer-button-wrapper">
        <button
          class="s-btn s-liff-theme-btn-outline s-btn-sm"
          type="button"
          @click="handleNegativeButtonClicked"
          v-if="hasPreviousStep()"
        >
          上一步
        </button>
        <button
          class="s-btn s-liff-theme-btn s-btn-sm"
          type="button"
          :disabled="nextStepIsDisabled"
          @click="handlePositiveButtonClicked">
          {{ textForProceedButton() }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker';
import { zh } from "vuejs-datepicker/dist/locale";
import bookingShiftApi from "@/apis/liff/v2/booking-shift";
import BookingPresetDataModel from "@/models/BookingPreset.model";
import BookingBusinessLogic from "@/utils/booking/BookingBusinessLogic";
import BookingWindow from '@/utils/booking/BookingWindow'
import LeadTime from "@/utils/booking/LeadTime";
import moment from 'moment';
import _ from "lodash";
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import Popup from "@/components/Page/Liff/Shared/Popup.vue";
import liff from "@line/liff";
import MemberButton from "@/components/Page/Liff/Shared/MemberButton.vue";
import SharedButton from "@/components/Page/Liff/Shared/Button.vue";

const bookingBusinessLogic = Vue.observable(new BookingBusinessLogic())
const bookingWindowChecker = new BookingWindow()
const leadTimeChecker = new LeadTime()

export default {
  components: {
    Datepicker,Popup,MemberButton,SharedButton
  },
  inject: ['back', 'proceed', 'hasPreviousStep', 'textForProceedButton'],
  data() {
    return {
      zh,
      date: null,
      time: null,
      staffShifts: [],
      branchShifts: [],
      existingBookingsOfCurrentDate: [],
      currentDisplayMonth: moment(),
      isFetchingMonthlyShifts: true,
    }
  },
  computed: {
    ...mapGetters('liffBookingWizard', ['selectedServices']),
    ...mapState('liffBookingWizard', ['bookingPreset', 'selectedBranch', 'selectedStaff', 'selectedServiceIds', 'occupancyChecker', 'extraFormData']),
    ...mapGetters('liffAuth', ['user']),
    dataModel() {
      return new BookingPresetDataModel(this.bookingPreset)
    },
    selectedDateInString() {
      return moment(this.date).format('YYYY-MM-DD')
    },
    selectedDateTime() {
      if (!this.date) return null
      const timeToUse = this.dataModel.startAtFieldFormatIsDate ? '00:00' : this.time
      if (!timeToUse) return null
      return moment(`${this.selectedDateInString} ${timeToUse}`)
    },
    endDateTimeForDisplay() {
      if (!this.date) return null
      const timeToUse = this.dataModel.startAtFieldFormatIsDate ? '23:59' : this.time
      if (!timeToUse) return null
      return bookingBusinessLogic.endDateTimeToSaveInWizard(`${this.selectedDateInString} ${timeToUse}`, this.serviceDuration)
        .format(this.dataModel.startAtFieldFormatIsDate ? 'yyyy-MM-DD' : 'yyyy-MM-DD HH:mm')
    },
    branchId() {
      return this.$route.query.branch_id;
    },
    staffId() {
      return this.$route.query.staff_id;
    },
    isCheckingOccupancy() {
      return this.occupancyChecker && this.occupancyChecker.isCheckingOccupancy;
    },
    isCheckingAvailability() {
      return this.occupancyChecker && this.occupancyChecker.isCheckingAvailability;
    },
    allShifts() {
      return [...this.staffShifts, ...this.branchShifts];
    },
    timeOptions() {
      const startField = this.dataModel.startTimeField || this.dataModel.baseStartTimeField;
        if (startField?.time_condition === 'specified') {
          // 分店有設就用分店，沒有就退回主設定
          const options = (startField?.time_options?.length
            ? startField.time_options
            : this.dataModel.baseStartTimeField?.time_options) || [];
          return options.map((timeOption) => ({
            text: timeOption.text,
            value: timeOption.value,
          }));
        }

      return bookingBusinessLogic.generateTimeOptionsForDate(moment(this.date).format('YYYY-MM-DD'))
        .map((timeOptionMoment) => {
          const timeValue = timeOptionMoment.format('HH:mm')
          return { value: timeValue, text: timeValue }
        })
    },
    serviceDuration() {
      return _.sumBy(this.selectedServices, 'duration');
    },
    passedAvailabilityCheck() {
      if (this.time === null || !this.occupancyChecker) {
        return true;
      }
      return [true, null].includes(this.occupancyChecker.getAvailabilityResult());
    },
    staffShiftIsEnabled() {
      return _.get(this.bookingPreset, 'config.enabled_selected_staff_shift', false);
    },
    firstAvailableDate() {
      const startOfMonth = moment().startOf('month');
      const endOfNextMonth = moment().add(2, 'months').endOf('month');

      for (let day = moment(startOfMonth); day.isBefore(endOfNextMonth); day.add(1, 'day')) {
        const currentDate = day.toDate();

        if (false === this.dateIsUnavailable(currentDate)) {
          return currentDate;
        }
      }

      return null;
    },
    reservationNotes() {
      const tips = _.get(this.bookingPreset, 'branch_config.basic.tips', _.get(this.bookingPreset, 'config.basic.tips', '')) || ''

      // 替換 [booking_tip]...[/booking_tip] 為按鈕 HTML
      // 這裡假設按鈕 class 為 's-btn-link' (您可以依據專案樣式調整)
      return tips.replace(
        /\[booking_tip\](.*?)\[\/booking_tip\]/g,
        '<button class="s-text-primary s-font-medium s-btn-reset booking-tip-button">$1</button>'
      )
    },
    bookingTip() {
      return _.get(this.bookingPreset, 'config.booking_tip', null)
    },
    selectedSchedule() {
      return {
        start_at: this.selectedDateTime?.format('YYYY-MM-DD HH:mm:ss') ?? this.selectedDateInString,
      }
    },
    selectedDateAndTimeLabel() {
      if (!this.date) return ''
      const dateLabel = moment(this.date).format('yyyy-MM-DD')
      const timeLabel = this.timeOptions.find((timeOption) => timeOption.value === this.time)?.text || this.time
      return timeLabel ? `${dateLabel} ${timeLabel}` : dateLabel
    },
    nextStepIsDisabled() {
      return !this.date
        || (!this.time && !this.dataModel.startAtFieldFormatIsDate)
        || !this.passedAvailabilityCheck
        || this.isCheckingOccupancy
        || this.isCheckingAvailability
    },
  },

  async mounted() {
    await this.fetchBookingPreset({ bookingPresetId: this.$route.params.bookingPresetId, branchId: this.branchId })
    bookingBusinessLogic.setBookingPresetDataModel(this.dataModel)
    this.setUpBookingWindowChecker()
    this.setUpLeadTimeChecker()

    await this.fetchMonthlyShifts(moment().toDate())
    this.date = this.firstAvailableDate
    this.handleDateChanged()

    // 加入BookingTip事件監聽
    this.$nextTick(() => {
      if (this.$refs.reservationNotes) {
        this.$refs.reservationNotes.addEventListener('click', this.handleNotesClick)
      }
    })
  },

   // 移除BookingTip事件監聽
   beforeDestroy() {
    if (this.$refs.reservationNotes) {
      this.$refs.reservationNotes.removeEventListener('click', this.handleNotesClick)
    }
  },

  methods: {
    ...mapActions('liffBookingWizard', ['fetchBookingPreset', 'selectSchedule', 'selectScheduleLabel']),
    deepGet: _.get,

    setUpBookingWindowChecker() {
      const bookingWindowLimitType = _.get(this.bookingPreset, 'config.basic.booking_window.type')
      const bookingWindowConfigSetters = {
        day: () => bookingWindowChecker.setWindowByDays(parseInt(_.get(this.bookingPreset, 'config.basic.booking_window.amount_day', 0))),
        minute: () => bookingWindowChecker.setWindowByMinutes(parseInt(_.get(this.bookingPreset, 'config.basic.booking_window.amount_minute', 0))),
      }
      _.invoke(bookingWindowConfigSetters, bookingWindowLimitType)
    },

    setUpLeadTimeChecker() {
      const leadTimeLimitType = _.get(this.bookingPreset, 'config.basic.lead_time.type')
      const leadTimeConfigSetters = {
        day: () => leadTimeChecker.setLeadDays(parseInt(_.get(this.bookingPreset, 'config.basic.lead_time.amount_day', 0))),
        minute: () => leadTimeChecker.setLeadMinutes(parseInt(_.get(this.bookingPreset, 'config.basic.lead_time.amount_minute', 0))),
      }
      _.invoke(leadTimeConfigSetters, leadTimeLimitType)
    },

    isOffDutyAtDate(date) {
      return this.allShifts.some(shift => {
        return shift.start_at &&
          shift.end_at &&
          moment(shift.start_at).isSameOrBefore(moment(date).startOf('day')) &&
          moment(shift.end_at).isSameOrAfter(moment(date).endOf('day').startOf('second'));
      });
    },

    isOffDutyAtTime(timeOption) {
      return this.allShifts.some(shift => {
        return shift.start_at &&
          shift.end_at &&
          moment(shift.start_at).isSameOrBefore(moment(this.getServiceEndTimeOfTimeOption(timeOption))) &&
          moment(shift.end_at).isSameOrAfter(moment(this.getServiceStartTimeOfTimeOption(timeOption)));
      });
    },

    timeIsOccupied(timeOption) {
      const startAt = this.getServiceStartTimeOfTimeOption(timeOption)
      const endAt = this.getServiceEndTimeOfTimeOption(timeOption)
      return this.occupancyChecker &&
        this.occupancyChecker.hasPerformedOccupancyCheck &&
        this.existingBookingsOfCurrentDate
          .filter(booking => startAt <= booking.end_at && endAt >= booking.start_at)
          .length >= this.occupancyChecker.occupancyMaximum()
    },

    getServiceStartTimeOfTimeOption(timeOption) {
      return moment(`${this.selectedDateInString} ${timeOption}`).format('YYYY-MM-DD HH:mm:ss')
    },

    getServiceEndTimeOfTimeOption(timeOption) {
      return bookingBusinessLogic
        .endDateTimeToSaveInWizard(`${this.selectedDateInString} ${timeOption}`, this.serviceDuration)
        .format('YYYY-MM-DD HH:mm:ss')
    },

    timeOptionToDateTime(timeOption) {
      return moment(`${this.selectedDateInString} ${timeOption}`).format('YYYY-MM-DD HH:mm:ss')
    },

    serviceCannotBeDoneBeforeShopIsClosed(timeOption) {
      const timeOptionStartMoment = moment(this.getServiceStartTimeOfTimeOption(timeOption))
      const timeOptionEndMoment = moment(this.getServiceEndTimeOfTimeOption(timeOption))
      const closingTimeMoment = moment(this.timeOptionToDateTime(this.dataModel.closingTimeForWeekday(timeOptionStartMoment.isoWeekday())))
      return timeOptionEndMoment.isAfter(closingTimeMoment)
    },

    dateHasPassed(date) {
      return moment(date).startOf('day').isBefore(moment().startOf('day'));
    },

    hasInsufficientBookingWindow(datetime) {
      const bookingWindowLimitType = _.get(this.bookingPreset, 'config.basic.booking_window.type') || null
      if (['none', null].includes(bookingWindowLimitType)) {
        return false // 設為不限制時，不檢查
      }
      return false === _.defaultTo(bookingWindowChecker.isAvailable(datetime), false)
    },

    hasInsufficientLeadTime(datetime) {
      return false === _.defaultTo(leadTimeChecker.isAvailable(datetime), false)
    },

    dateIsUnavailable(date) {
      return [
        this.dateHasPassed(date),
        this.isOffDutyAtDate(date),
        this.hasInsufficientBookingWindow(moment(date).startOf('day').format('YYYY-MM-DD HH:mm:ss')),
        this.hasInsufficientLeadTime(moment(date).endOf('day').format('YYYY-MM-DD HH:mm:ss')),
        this.branchIsOnHoliday(date),
      ].some(condition => true === condition)
    },

    timeIsUnavailable(timeOption) {
      return [
        this.isOffDutyAtTime(timeOption),
        this.serviceCannotBeDoneBeforeShopIsClosed(timeOption),
        this.hasInsufficientBookingWindow(this.getServiceEndTimeOfTimeOption(timeOption)),
        this.hasInsufficientLeadTime(this.timeOptionToDateTime(timeOption)),
        this.timeIsOccupied(timeOption),
        this.outOfBusinessHours(timeOption),
      ].some(condition => true === condition)
    },

    branchIsOnHoliday(date) {
      return this.dataModel.isOfficialHolidayOfBranch(moment(date).isoWeekday())
    },

    outOfBusinessHours(timeOption) {
      const timeOptionMoment = moment(this.timeOptionToDateTime(timeOption))

      return this.dataModel.isOfficialHolidayOfBranch(timeOptionMoment.isoWeekday()) ||
        false === this.dataModel.targetTimeIsBetweenBusinessHours(timeOptionMoment)
    },

    selectTime(timeOption) {
      if (this.timeIsUnavailable(timeOption)) {
        return;
      }
      this.time = timeOption;

      _.invoke(this.occupancyChecker, 'receiveTimeRange',
        this.getServiceStartTimeOfTimeOption(timeOption),
        this.getServiceEndTimeOfTimeOption(timeOption)
      );
    },

    handleCalendarOpened() {
      this.currentDisplayMonth = moment(this.date);
    },

    async handleMonthChange(date) {
      const newMonth = moment(date);
      if (this.currentDisplayMonth.isSame(newMonth, 'month')) {
        return;
      }

      this.currentDisplayMonth = newMonth;
      await this.fetchMonthlyShifts(date);
    },

    async fetchMonthlyShifts(date) {
      const startOfMonth = moment(date).startOf('month').format('YYYY-MM-DD');
      const endOfMonth = moment(date).endOf('month').format('YYYY-MM-DD');

      this.isFetchingMonthlyShifts = true
      await Promise.all([
        (async () => {
          if (this.staffShiftIsEnabled && this.staffId) {
            this.staffShifts = await bookingShiftApi.get({
              booking_preset_id: this.$route.params.bookingPresetId,
              staff_id: this.staffId,
              start_at: startOfMonth,
              end_at: endOfMonth
            })
          }
        })(),
        (async () => {
          if (this.branchId) {
            this.branchShifts = await bookingShiftApi.get({
              booking_preset_id: this.$route.params.bookingPresetId,
              branch_id: this.branchId,
              start_at: startOfMonth,
              end_at: endOfMonth
            })
          }
        })(),
      ])
      this.isFetchingMonthlyShifts = false
    },

    resetDate() {
      this.date = this.firstAvailableDate;
      this.handleDateChanged()
    },

    handleNegativeButtonClicked() {
      this.back()
    },

    handlePositiveButtonClicked() {
      this.selectSchedule({
        start_at: this.selectedDateTime.format('YYYY-MM-DD HH:mm:ss')
      });
      this.selectScheduleLabel(this.selectedDateAndTimeLabel);

      this.proceed()
    },

    async handleDateChanged() {
      if (this.dataModel.startAtFieldFormatIsDate) {
        this.time = '00:00'
      } else {
        this.time = null
      }

      this.handleMonthChange(this.date)

      await _.invoke(this.occupancyChecker, 'receiveTimeRange',
        moment(this.date).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        moment(this.date).endOf('day').format('YYYY-MM-DD HH:mm:ss')
      );
      this.existingBookingsOfCurrentDate = _.get(_.invoke(this.occupancyChecker, 'getOccupancyResult'), 'bookings', [])
    },

    // BookingTip事件處理方法
    handleNotesClick(e) {
      // 檢查點擊的是否為我們的目標按鈕
      if (e.target && e.target.classList.contains('booking-tip-button')) {
        this.bookingTipHandler()
      }
    },

    async bookingTipHandler() {
      await this.sendLiffMessage()
      this.$refs.bookingTipPopup.open()
    },

    async sendLiffMessage() {
      const sendMessages = _.get(this.bookingPreset, 'config.booking_tip.send_messages', null)
      if (sendMessages.enabled && sendMessages.text) {
        const messageText = sendMessages.text

        // 處理 {{$booking['x']}} - 加入 \s* 允許括號周圍有空格或無空格
        let result = messageText.replace(/\{\{\$booking_data\s*\['(.+?)'\]\s*\}\}/g, (_, key) => {

          switch(key) {
            case 'selectedBranch':
              return this.selectedBranch.name
            case 'selectedServices':
              return this.selectedServices.map((service) => service.title).join('，')
            case 'selectedStaff':
              return this.selectedStaff.name
            case 'selectedSchedule':
              return this.selectedSchedule.start_at
            default:
              return this.extraFormData[key] || "";
          }
        });

        // 處理 {{$customer['extra']['x']}}
        result = result.replace(/\{\{\$customer\s*\['extra'\]\s*\['(.+?)'\]\s*\}\}/g, (_, key) => { return this.user.extra[key] || ""; });

        // 處理 {{$customer['x']}}
        result = result.replace(/\{\{\$customer\s*\['(.+?)'\]\s*\}\}/g, (_, key) => { return this.user[key] || ""; });

        console.log(result);

        await liff
          .sendMessages([
            {
              type: "text",
              text: result,
            },
          ])
          .then(() => {
            console.log("message sent");
          })
          .catch((err) => {
            console.log("error", err);
          });
        return;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.liff-reservation-schedule {
  padding-bottom: 150px;

  &__branch-staff {
    border: 1px solid var(--s-primary);
    border-radius: 100px;
    display: inline-flex;
    padding: 4px 15px;
    margin: auto;
    gap: 40px;
    align-items: center;

    &__img {
      display: flex;
      align-items: center;
      line-height: 0;      
      img{        
        width: 20px;
        height: 20px;
        border-radius: 50%; 
        margin-right: 5px;
      }
    }
  }
  &__services {
    border: 1px solid  var(--liff-header-color);
    border-radius: 100px;
    display: inline-flex;
    padding: 4px 15px;
    margin: auto;    
    align-items: center;

    &__img {
      line-height: 0;
      img {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        margin-right: 5px;
      }
    }
  }

  &__date {
    position: relative;
  }

  ::v-deep .vdp-datepicker__calendar {
    border: 1px solid #E5E5EA;
    border-radius: 10px;
    box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.15);
    padding: 10px;

    .cell {
      position: relative;
      &.today:not(.selected) {
        background: transparent;
        color: var(--s-primary);
        border: 1px solid var(--s-primary);
        border: none;

        &::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 37px;
          height: 37px;
          //background: var(--s-primary);
          opacity: 0.3;
          border-radius: 50%;
        }

        &::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 37px;
          height: 37px;
          border: 2px dotted var(--s-primary);
          border-radius: 50%;
        }
      }

      &:not(.blank):not(.disabled).day:hover,
      &.selected,
      &.selected:hover {
        background: transparent;
        color: #fff;
        border: none;
        z-index: 0;

        &::before {
          content: '';
          position: absolute;
          background: var(--s-primary);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 37px;
          height: 37px;
          border: 1px solid var(--s-primary);
          border-radius: 50%;
          z-index: -1;
          box-shadow: 0 4px 4px 0 rgba(44, 44, 46, 0.15);
        }
      }
    }
  }

  &__notice {
    margin-top: 16px;

    &-divider {
      background: #E5E5EA;
      width: 100%;
      height: 1px;
      left: 0;
      position: absolute;
    }
  }

  &__timer {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    position: relative;
    min-height: 80px; // for loading overlay
  }

  &__time-input {
    display: none;
  }

  &__time-option {
    border: 1px solid #E5E5EA;
    width: calc((100% - 16px) / 3);
    text-align: center;
    font-weight: 500;
    padding: 0 30px;
    border-radius: 10px;
    box-shadow: 0 2px 4px 0 rgba(0,0,0,0.1);
    height: 36px;
    line-height: 34px;
    white-space: nowrap;
    &.selected {
      border: 3px solid var(--s-primary);
      line-height: 30px;
    }
    &.disabled {
      opacity: 0.5;
      background-color: #f5f5f5;
      cursor: not-allowed;
      border: 1px solid #E5E5EA;
    }
  }
}

::v-deep .popup__close {
    top: -4rem;
    position: absolute;
    left: 0;
    right: 0;
}
</style>
